import {
  useSuspenseQuery,
  useMutation,
  useQueryClient,
  useSuspenseQueries,
  QueryClient,
} from "@tanstack/react-query"
import { axiosInstance } from "@/api/axios"
import {
  GetDepartmentsResponse,
  GetSelectedDepartmentsResponse,
  SelectedDepartment,
  UpdateSelectedDepartmentRequest,
  CreateSelectedDepartmentRequest,
  GetPersonnelDataResponse,
  PostBody,
  ColumnSwitchRequest,
  PersonnelTotalResultsResponse,
} from "./types"
import { PeriodType, PeriodTypeQuery, PeriodTypeView } from "@/types"
import { getSingleFinancialModelSettingsV2 } from "@/feature/financial-model/api"
import { FinancialModelSettingsStatus } from "@/feature/financial-model/api/types"
import {
  createQueryKey,
  FINANCIAL_MODEL_SETTINGS_BASE_KEY,
  invalidateGlobalsQueryKey,
  PERSONNEL_BASE_KEY,
  TOTALS_KEY,
} from "@/api/query-keys"

const getDepartments = async (
  financialModelId: string
): Promise<GetDepartmentsResponse> => {
  const response = await axiosInstance.get<GetDepartmentsResponse>(
    `/financial-models/${financialModelId}/departments`
  )
  return response.data
}

export const useGetDepartmentsSuspenseQuery = (financialModelId: string) =>
  useSuspenseQuery({
    queryKey: createQueryKey({
      baseKey: PERSONNEL_BASE_KEY,
      keys: [financialModelId, `departments`],
    }),
    queryFn: () => getDepartments(financialModelId),
  })

const getSelectedDepartments = async (
  financialModelId: string
): Promise<GetSelectedDepartmentsResponse> => {
  const response = await axiosInstance.get<GetSelectedDepartmentsResponse>(
    `/financial-models/${financialModelId}/selected-departments`
  )
  return response.data
}

export const useGetSelectedDepartmentsSuspenseQuery = (
  financialModelId: string
) =>
  useSuspenseQuery({
    queryKey: createQueryKey({
      baseKey: PERSONNEL_BASE_KEY,
      keys: [financialModelId, `selected-departments`],
    }),
    queryFn: () => getSelectedDepartments(financialModelId),
    select: (data) => data.filter((dep) => dep.is_selected),
  })

const createCustomDepartment = async (
  financialModelId: string,
  department: CreateSelectedDepartmentRequest
): Promise<SelectedDepartment> => {
  const response = await axiosInstance.post<SelectedDepartment>(
    `/financial-models/${financialModelId}/departments`,
    department
  )
  return response.data
}

export const useCreateCustomDepartmentMutation = (financialModelId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (department: CreateSelectedDepartmentRequest) =>
      createCustomDepartment(financialModelId, department),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: createQueryKey({
          baseKey: PERSONNEL_BASE_KEY,
          keys: [financialModelId, `departments`],
        }),
      })
      queryClient.invalidateQueries({
        queryKey: createQueryKey({
          baseKey: PERSONNEL_BASE_KEY,
          keys: [financialModelId, `selected-departments`],
        }),
      })
      queryClient.resetQueries({
        queryKey: createQueryKey({
          baseKey: TOTALS_KEY,
          keys: [financialModelId],
        }),
      })
      invalidateGlobalsQueryKey([FINANCIAL_MODEL_SETTINGS_BASE_KEY])
    },
  })
}

const updateSelectedDepartment = async (
  financialModelId: string,
  department: UpdateSelectedDepartmentRequest
): Promise<string> => {
  const response = await axiosInstance.put<string>(
    `/financial-models/${financialModelId}/selected-departments`,
    department
  )
  return response.data
}

export const useUpdateSelectedDepartmentMutation = (
  financialModelId: string
) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (department: UpdateSelectedDepartmentRequest) =>
      updateSelectedDepartment(financialModelId, department),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: createQueryKey({
          baseKey: PERSONNEL_BASE_KEY,
          keys: [financialModelId],
        }),
      })
      queryClient.resetQueries({
        queryKey: createQueryKey({
          baseKey: TOTALS_KEY,
          keys: [financialModelId],
        }),
      })
      invalidateGlobalsQueryKey([FINANCIAL_MODEL_SETTINGS_BASE_KEY])
    },
  })
}

const deleteSelectedDepartment = async (
  financialModelId: string,
  departmentId: string
): Promise<string> => {
  const response = await axiosInstance.delete<string>(
    `/financial-models/${financialModelId}/departments/${departmentId}`
  )
  return response.data
}

export const useDeleteSelectedDepartmentMutation = (
  financialModelId: string
) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (departmentId: string) =>
      deleteSelectedDepartment(financialModelId, departmentId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: createQueryKey({
          baseKey: PERSONNEL_BASE_KEY,
          keys: [financialModelId],
        }),
      })
      queryClient.resetQueries({
        queryKey: createQueryKey({
          baseKey: TOTALS_KEY,
          keys: [financialModelId, `personnel`],
        }),
      })
      invalidateGlobalsQueryKey([FINANCIAL_MODEL_SETTINGS_BASE_KEY])
    },
  })
}

const getPersonnelData = async (
  financialModelId: string,
  departmentId: string,
  period: PeriodTypeQuery,
  view: PeriodTypeView
): Promise<GetPersonnelDataResponse> => {
  const response = await axiosInstance.get<GetPersonnelDataResponse>(
    `/financial-models/${financialModelId}/personnel-data`,
    {
      params: {
        department_id: departmentId,
        period_type: period,
        view,
      },
    }
  )
  return response.data
}
export const useGetPersonnelDepartmentDataSuspenseQuery = ({
  financialModelId,
  departmentId,
  period,
  view,
}: {
  financialModelId: string
  departmentId: string
  period: PeriodTypeQuery
  view: PeriodTypeView
}) =>
  useSuspenseQuery({
    queryKey: createQueryKey({
      baseKey: PERSONNEL_BASE_KEY,
      keys: [financialModelId, `personnel-data`, departmentId, period, view],
    }),
    queryFn: () =>
      getPersonnelData(financialModelId, departmentId, period, view),
  })

const postPersonnelData = async (
  financialModelId: string,
  data: PostBody
): Promise<string> => {
  const response = await axiosInstance.post<string>(
    `/financial-models/${financialModelId}/personnel-data`,
    data
  )
  return response.data
}

export const getPostPersonnelDataMutationArgs = (
  financialModelId: string,
  queryClient: QueryClient
) => ({
  mutationFn: (data: PostBody) => postPersonnelData(financialModelId, data),
  onSuccess: (_: unknown, variables: PostBody) => {
    queryClient.invalidateQueries({
      queryKey: createQueryKey({
        baseKey: PERSONNEL_BASE_KEY,
        keys: [financialModelId, `personnel-data`, variables.department_id],
      }),
    })
    queryClient.invalidateQueries({
      queryKey: [`financial-model-settings-v2`],
    })
    queryClient.invalidateQueries({
      queryKey: createQueryKey({
        baseKey: PERSONNEL_BASE_KEY,
        keys: [financialModelId, `selected-departments`],
      }),
    })
    queryClient.resetQueries({
      queryKey: createQueryKey({
        baseKey: TOTALS_KEY,
        keys: [financialModelId, `personnel`],
      }),
    })
  },
})

export const usePostPersonnelDataMutation = (financialModelId: string) => {
  const queryClient = useQueryClient()
  return useMutation(
    getPostPersonnelDataMutationArgs(financialModelId, queryClient)
  )
}

const switchPersonnelRowType = async (
  financialModelId: string,
  data: Omit<ColumnSwitchRequest, `financial_model_id`>
): Promise<string> => {
  const response = await axiosInstance.post<string>(
    `/financial-models/${financialModelId}/personnel-data/config/${data.column_name}`,
    undefined,
    {
      params: {
        department_id: data.department_id,
        period_type: data.period_type,
        col_type: data.col_type,
        year: data.year,
        month: data.month,
      },
    }
  )
  return response.data
}
export const useSwitchPersonnelRowTypeMutation = (financialModelId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: Omit<ColumnSwitchRequest, `financial_model_id`>) =>
      switchPersonnelRowType(financialModelId, data),
    mutationKey: [`switch-personnel-row-type`],
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: createQueryKey({
          baseKey: PERSONNEL_BASE_KEY,
          keys: [financialModelId, `personnel-data`, variables.department_id],
        }),
      })
      queryClient.invalidateQueries({
        queryKey: [`financial-model-settings-v2`],
      })
    },
  })
}

type DepartmentWithSetup = SelectedDepartment & {
  status: FinancialModelSettingsStatus
}

export const useGetPersonnelDepartmentSetupQuery = (
  financialModelId: string,
  period: PeriodType
) =>
  useSuspenseQueries({
    queries: [
      {
        queryKey: createQueryKey({
          baseKey: PERSONNEL_BASE_KEY,
          keys: [financialModelId, `selected-departments`],
        }),
        queryFn: () => getSelectedDepartments(financialModelId),
      },
      {
        queryKey: [
          `financial-model-settings-v2`,
          `single`,
          financialModelId,
          `personnel_costs`,
          period,
        ],
        queryFn: () =>
          getSingleFinancialModelSettingsV2({
            id: financialModelId,
            module: `personnel_costs`,
            section: period,
          }),
      },
    ],
    combine: ([departments, settings]) => ({
      data: departments?.data?.reduce((acc, department) => {
        if (!department.is_selected) return acc
        const setting = settings?.data?.personnel_costs?.settings.find(
          (s) => s.value === department.department.id
        )
        acc.push({ ...department, status: setting?.status ?? `not_started` })
        return acc
      }, [] as DepartmentWithSetup[]),
      isSuccess: departments?.isSuccess && settings?.isSuccess,
      isError: departments?.isError || settings?.isError,
      error: departments?.error || settings?.error,
      isLoading: departments?.isLoading || settings?.isLoading,
    }),
  })

const getPersonnelTotalResults = async (
  financialModelId: string,
  view: PeriodTypeView
): Promise<PersonnelTotalResultsResponse> => {
  const response = await axiosInstance.get<PersonnelTotalResultsResponse>(
    `/financial-models/${financialModelId}/personnel-data/total-results`,
    {
      params: {
        view: view ?? `monthly`,
      },
    }
  )
  return response.data
}

export const useGetPersonnelTotalResultsSuspanseQuery = ({
  financialModelId,
  view,
}: {
  financialModelId: string
  view: PeriodTypeView
}) =>
  useSuspenseQuery({
    queryKey: createQueryKey({
      baseKey: TOTALS_KEY,
      keys: [financialModelId, `personnel`, view],
    }),
    queryFn: () => getPersonnelTotalResults(financialModelId, view),
    staleTime: 0,
  })
